import { AppDataComponent, CompRef } from '@wix/document-services-types';
import {
  AppInstallOption,
  UniversalSlotPointer,
  WidgetPointer,
} from '@wix/editor-platform-host-integration-apis';
import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  HEADER_SYSTEM_SLOT,
  ensureSystemHeaderSlotComponent,
} from '../../../plugins/headerPluginsAPI/systemHeaderSlot';
import { SlotPointer } from '../../../plugins/slotPointer';
import { createHeaderPluginsApi } from '../../../plugins/headerPluginsAPI';
import { HeaderPluginService } from '../../../plugins/headerPluginService';
import { getPluginsForAutoPopulation } from './pluginService';

const HEADER_PLUGINS_EXPERIMENT = 'specs.editor-platform.headerPlugins';

const getHeaderPlugins = (components: AppDataComponent[]) =>
  getPluginsForAutoPopulation(
    components,
    (slotId) => slotId === HEADER_SYSTEM_SLOT.id,
    // The predicate above narrows placementsSlotIds to the header slot only, so
    // plugins not targeting it are left with an empty array and dropped here.
    // TODO: once se_pluggable_slots is merged this filter will not be needed.
  ).filter((plugin) => plugin.placementsSlotIds?.length);

export async function autoPopulateHeaderPlugins(
  context: ExtendedPlatformContext,
  appsOptions: AppInstallOption,
  appDefinitionId: string,
  components: AppDataComponent[],
): Promise<void> {
  if (!context.experimentsAPI.enabled(HEADER_PLUGINS_EXPERIMENT)) {
    return Promise.resolve();
  }

  const headerPlugins = getHeaderPlugins(components);

  if (!headerPlugins.length) {
    return Promise.resolve();
  }

  // Header auto-population is a best-effort, experiment-gated decoration step.
  // Guard the whole flow so that a failure here (e.g. ensuring the system header
  // slot throws) is logged but never rejects the host app installation.
  try {
    const headerSlotHostCompRef = await ensureSystemHeaderSlotComponent(
      context.documentServices,
    );
    if (!headerSlotHostCompRef) {
      return;
    }

    const headerSlotPointer: UniversalSlotPointer = {
      hostCompRef: headerSlotHostCompRef,
      slotId: HEADER_SYSTEM_SLOT.id,
    };

    for (const headerPlugin of headerPlugins) {
      if (!headerPlugin.referenceComponentId) {
        console.warn(
          'Header plugin reference component id not found, skipping auto population of header plugin',
          headerPlugin,
        );
        continue;
      }

      const widgetRef: WidgetPointer = {
        appDefinitionId,
        widgetId: headerPlugin.referenceComponentId,
      };

      await handleHeaderPlugin(
        context,
        appsOptions,
        headerSlotPointer,
        widgetRef,
      ).catch((e) => {
        console.error('Failed to auto populate header plugin', {
          error: e,
          slotPointer: headerSlotPointer,
          widgetRef,
        });
      });
    }
  } catch (e) {
    console.error(
      'Failed to auto populate header plugins, skipping header plugins auto population',
      { error: e, appDefinitionId },
    );
  }
}

async function handleHeaderPlugin(
  context: ExtendedPlatformContext,
  appsOptions: AppInstallOption,
  slotPointer: UniversalSlotPointer,
  widgetRef: WidgetPointer,
): Promise<CompRef | null> {
  const headerPluginApi = createHeaderPluginsApi(
    context.documentServices,
    context.experimentsAPI,
  );
  const service = new HeaderPluginService(headerPluginApi);
  const biOrigin = appsOptions?.biData?.origin || 'appMarket';
  return service.installHeaderPlugin(
    context,
    SlotPointer.create(
      context.documentServices,
      context.manifestAPI,
      context.experimentsAPI,
      slotPointer,
    ),
    widgetRef,
    {
      biOrigin,
      controlMode: 'user',
    },
  );
}
